import { ol_VectorLayer, ol_control, ol_Coordinate, ol_events_condition, ol_Extent, ol_Feature, ol_FeatureLike, ol_Map, ol_MapBrowserPointerEvent, ol_source, ol_style, ol_View, ol_geom, ol_interaction, ol_layer } from "@omnia/exports/openlayers";
import { isFunction } from "../omnia";
import { GcpContext } from "./gcp-context";
import { GcpPoint } from "./gcp-point";
import { IGcpMap } from "./types";

export abstract class GcpMap implements IGcpMap {

    protected olmap: ol_Map;
    protected pointsLayer: ol_VectorLayer;

    private _translateInteraction: ol_interaction.Translate;
    protected translatableFeatures: ol_Feature[] = [];

    private _editMode: boolean = true;

    constructor(protected context: GcpContext, protected canRotate: boolean) {

    }

    abstract getPointFeature(point: GcpPoint): ol_Feature;
    abstract addNewPoint(coord: ol_Coordinate): GcpPoint;

    public getMap(): ol_Map {
        return this.olmap;
    }

    public updateSize() {
        this.olmap.updateSize();
    }

    public refresh() {
        this.pointsLayer.setStyle(null);
        this.pointsLayer.setStyle((feature, resolution) => this.getPointStyle(feature, resolution));
    }

    get editMode(): boolean {
        return this._editMode;
    }

    set editMode(value: boolean) {
        this._editMode = value;
        this._translateInteraction.setActive(value);
    }

    private checkMustCompletePoint(evt: ol_MapBrowserPointerEvent): boolean {

        if (!this.context.canAddNewPoints()) return false;

        let p = this.context.currentPoint;
        if (!p) return false;
        if (this.getPointFeature(p)) return false;

        if (ol_events_condition.click(evt)) {

            let feature = this.context.completePoint(p, evt.coordinate);

            this.pointsLayer.getSource().addFeature(feature);

            this.context.setCurrentPoint(null);

            return true;

        }

    }

    private checkAddNewPoint(evt: ol_MapBrowserPointerEvent): boolean {

        if (!this._editMode) return false;
        if (!this.context.canAddNewPoints()) return false;

        let p = this.context.currentPoint;
        if (p && (!p.isComplete())) return false;

        let originalEvent: MouseEvent = (<any>evt).originalEvent;

        let ctrl = originalEvent && (originalEvent.ctrlKey);

        if (this.context.addPointMode || ctrl) {

            if (ol_events_condition.click(evt)) {

                let point = this.addNewPoint(evt.coordinate);

                if (point) {

                    this.context.setCurrentPoint(point);

                    let feature = this.getPointFeature(point);
                    this.pointsLayer.getSource().addFeature(feature);

                    return true;
                }

            }

        }

    }

    protected setupAddPointInteraction() {

        let interaction = new ol_interaction.Interaction({

            handleEvent: (evt: ol_MapBrowserPointerEvent) => {

                if (this.checkMustCompletePoint(evt)) {
                    return false;
                }

                if (this.checkAddNewPoint(evt)) {
                    return false;
                }

                return true;

            }

        });

        return interaction;

    }

    public setupMap(targetElement: HTMLElement) {

        this.pointsLayer = this.createPointsLayer();

        let addPointInteraction = this.setupAddPointInteraction();

        let translateInteraction = this._translateInteraction = new ol_interaction.Translate(<any>{
            layers: [this.pointsLayer],
        });

        let rotateInteraction = (!this.canRotate) ? null : new ol_interaction.DragRotate({
            condition: ol_events_condition.shiftKeyOnly
        });

        let interactions = ol_interaction.defaults()
            .getArray()
            .filter(i => !(i instanceof ol_interaction.DragBox))
            .concat([
                rotateInteraction,
                translateInteraction,
                addPointInteraction
            ])
            .filter(i => !!i);

        let controls = ol_control.defaults({
            rotate: false,
        });

        if (this.canRotate) {
            controls.push(omnia.map.RotateControl.create());
        }

        let view = new ol_View({});

        let map = new ol_Map({
            layers: [],
            target: targetElement,
            controls: controls,
            interactions: interactions,
            view: view,
        });

        this.olmap = map;

    }

    protected getPointStyle(feature: ol_FeatureLike, resolution: number): ol_style.Style[] {

        let point = <GcpPoint>feature.get("data");

        if (point && (point instanceof GcpPoint)) {

            let fill = (point == this.context.currentPoint ? "#FF9800" : "white");

            return [
                new ol_style.Style({

                    image: new ol_style.Circle({
                        radius: 13,
                        fill: new ol_style.Fill({
                            color: fill
                        }),
                    }),

                    text: new ol_style.Text(<any>{
                        font: "bold 14px sans-serif",
                        text: point.number.toString(),
                        overflow: true
                    })
                })
            ];

        }

    }

    protected createPointsLayer() {

        let source = new ol_source.Vector({});

        let layer = new ol_layer.Vector({
            source: source,
            style: (feature, resolution) => this.getPointStyle(feature, resolution)
        });

        return layer;
    }

    protected viewExtent(extent: ol_Extent, callback: () => void = null) {

        setTimeout(() => {

            this.olmap.updateSize();

            let size = this.olmap.getSize();
            let view = this.olmap.getView();

            view.fit(extent, { size });

            if (isFunction(callback)) {
                callback();
            }

        }, 100);

    }

    public updatePointsLayer(points: GcpPoint[]) {

        let features = points.map(p => this.getPointFeature(p));

        let source = this.pointsLayer.getSource();
        source.clear();
        source.addFeatures(features);

    }

    public centerFeature(feature: ol_Feature) {
        if (!feature) return;
        let point = feature.getGeometry();
        if (point instanceof ol_geom.Point) {
            let coords = point.getCoordinates();
            this.olmap.getView().setCenter(coords);
        }
    }
}
